import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { checkEmail, signIn, signUp } from '../../Module/Auth/Presentation/authActions';
import { useAppDispatch } from '../../store/hooks';
import { PATHS } from '../../routes/paths';

export type FundraiserType = 'individual' | 'group' | 'foreigner';
type EmailCheckStatus = 'idle' | 'checking' | 'available' | 'duplicate' | 'error';
const SUPPRESS_POST_SIGNUP_MODALS_KEY = 'missionfund.suppressPostSignupModals';
/** 회원가입 환영 화면에서 '나중에 하기'로 메인 프로젝트로 갈 때 카카오 안내 모달을 잠시 띄우지 않음 */
const SIGNUP_SKIP_ACCOUNT_FIRST_KEY = 'missionfund.signupSkippedAccountFirst';

export const useSignup = () => {
  const { t } = useTranslation('translation');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const snsInfo = location.state as {
    sns_id?: string;
    sns_phone?: string;
    sns_thumbnail?: string;
    sns_name?: string;
    sns_birth?: string;
  } | undefined;

  const [step, setStep] = useState(0); // 0: Type Selection, 1: Email, 2: Password, 3: Additional Info, 4: Welcome
  const [fundraiserType, setFundraiserType] = useState<FundraiserType>('individual');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [category, setCategory] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [emailCheckStatus, setEmailCheckStatus] = useState<EmailCheckStatus>('idle');
  const [emailCheckMessage, setEmailCheckMessage] = useState<string | undefined>(undefined);
  const latestEmailCheckId = useRef(0);

  const trimmedEmail = useMemo(() => email.trim(), [email]);
  const isEmailFormatValid = useCallback((value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value), []);
  const isEmailAvailable = emailCheckStatus === 'available';

  useEffect(() => {
    if (!trimmedEmail) {
      setEmailCheckStatus('idle');
      setEmailCheckMessage(undefined);
      return;
    }

    if (!isEmailFormatValid(trimmedEmail)) {
      setEmailCheckStatus('error');
      setEmailCheckMessage(t('page.signup.validation.email.invalid'));
      return;
    }

    const currentCheckId = ++latestEmailCheckId.current;
    const timer = window.setTimeout(async () => {
      setEmailCheckStatus('checking');
      setEmailCheckMessage(t('page.signup.validation.email.checking'));

      try {
        const result = await dispatch(checkEmail({ email: trimmedEmail })).unwrap();
        if (latestEmailCheckId.current !== currentCheckId) return;

        if (result.available) {
          setEmailCheckStatus('available');
          setEmailCheckMessage(t('page.signup.validation.email.available'));
          return;
        }

        setEmailCheckStatus('duplicate');
        setEmailCheckMessage(t('page.signup.validation.email.duplicate'));
      } catch (e) {
        if (latestEmailCheckId.current !== currentCheckId) return;
        const msg = typeof e === 'string' ? e : (e as Error)?.message;
        setEmailCheckStatus('error');
        setEmailCheckMessage(msg || t('page.signup.validation.email.error'));
      }
    }, 500);

    return () => {
      window.clearTimeout(timer);
    };
  }, [dispatch, isEmailFormatValid, t, trimmedEmail]);

  const passwordMessage = useMemo(() => {
    if (password && passwordConfirm && password !== passwordConfirm) {
      return t('page.signup.validation.passwordMismatch');
    }
    return undefined;
  }, [password, passwordConfirm, t]);

  const handleNext = useCallback(() => {
    setStep((prev) => {
      if (prev === 1 && !isEmailAvailable) return prev;
      return prev + 1;
    });
  }, [isEmailAvailable]);

  const handlePrev = useCallback(() => {
    setStep((prev) => {
      if (prev <= 0) {
        navigate(-1);
        return 0;
      }
      return prev - 1;
    });
  }, [navigate]);

  const handleConfirm = useCallback(async () => {
    if (isSubmitting) return;
    if (!isEmailAvailable) {
      setError(emailCheckMessage || t('page.signup.errors.default'));
      return;
    }

    try {
      setIsSubmitting(true);
      setError(undefined);

      const partner = (process.env.REACT_APP_PARTNER as string | undefined) ?? 'OMF';
      const trimmedName = name.trim();
      const isIndividual = fundraiserType === 'individual';
      // 개인 회원: 전체 이름을 first_name에만 저장 (어드민에서 이름 하나로 표시)
      // 단체 회원: 소속 단체명만 organization에 저장, first/last_name은 비움
      const first_name = isIndividual ? trimmedName : '';
      const last_name = '';

      const res = await dispatch(
        signUp({
          first_name,
          last_name,
          email: email.trim(),
          partner,
          user_passwd: password,
          phone: phoneNumber.trim(),
          // 회원 유형은 상세 코드(Missionary/NGO 등)로 저장
          user_type: category,
          user_desc: category,
          authentication_by: 'No',
          // 단체 회원: 입력한 단체명을 organization 필드에도 전달
          ...(isIndividual ? {} : { organization: trimmedName }),
          ...(snsInfo?.sns_id
            ? {
                sns_id: snsInfo.sns_id,
                sns_phone: snsInfo?.sns_phone,
                sns_thumbnail: snsInfo?.sns_thumbnail,
                sns_name: snsInfo?.sns_name,
                sns_birth: snsInfo?.sns_birth,
              }
            : {}),
        }),
      ).unwrap();

      const ok = res?.result_message?.acknowledged ?? true;
      if (!ok) throw new Error(t('page.signup.errors.default'));

      if (typeof window !== 'undefined') {
        window.localStorage.setItem(SUPPRESS_POST_SIGNUP_MODALS_KEY, '1');
      }
      try {
        await dispatch(signIn({ user_email: email.trim(), password, partner })).unwrap();
      } catch {
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(SUPPRESS_POST_SIGNUP_MODALS_KEY);
        }
        navigate(PATHS.LOGIN, {
          replace: true,
          state: { fromSignup: true, prefilledEmail: email.trim() },
        });
        return;
      }

      setStep(4);
    } catch (e) {
      const msg = typeof e === 'string' ? e : (e as Error)?.message;
      setError(msg || t('page.signup.errors.default'));
    } finally {
      setIsSubmitting(false);
    }
  }, [category, dispatch, email, emailCheckMessage, fundraiserType, isEmailAvailable, isSubmitting, name, navigate, password, phoneNumber, snsInfo, t]);

  const goToLogin = useCallback(() => {
    const userId = window.localStorage.getItem('missionfund.userId');
    navigate(userId ? PATHS.DASHBOARD : PATHS.LOGIN);
  }, [navigate]);

  const goToAccount = useCallback(() => {
    navigate(PATHS.MY_ACCOUNT, { replace: true });
  }, [navigate]);

  const goToDashboard = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(SIGNUP_SKIP_ACCOUNT_FIRST_KEY, '1');
    }
    navigate(PATHS.PROJECT_MANAGEMENT, { replace: true });
  }, [navigate]);

  return {
    step,
    setStep,
    fundraiserType,
    setFundraiserType,
    email,
    setEmail,
    password,
    setPassword,
    passwordConfirm,
    setPasswordConfirm,
    name,
    setName,
    phoneNumber,
    setPhoneNumber,
    category,
    setCategory,
    handleNext,
    handlePrev,
    handleConfirm,
    isSubmitting,
    error,
    emailCheckStatus,
    emailCheckMessage,
    isEmailAvailable,
    passwordMessage,
    goToLogin,
    goToAccount,
    goToDashboard,
  };
};

